/**
 * Filters an array of objects to only include items that have all required fields filled.
 * Supports dot notation for nested fields (e.g. 'image.url').
 */
export function filterFilledItems<T extends object>(
  items: T[] | null | undefined,
  requiredFields: (keyof T | string)[],
  fieldValidators?: FieldValidator<T>[],
): T[] {
  if (!items?.length) return [];

  return items.filter((item) => {
    const fieldsValid = requiredFields.every((field) => {
      const value = getNestedValue(item, field as string);
      return value !== null && value !== undefined && value !== "";
    });

    const validatorsValid = fieldValidators
      ? fieldValidators.every(({ field, validate }) =>
          validate(getNestedValue(item, field as string)),
        )
      : true;

    return fieldsValid && validatorsValid;
  });
}

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current, key) => {
    if (current === null || current === undefined) return undefined;
    return (current as Record<string, unknown>)[key];
  }, obj);
}

// Reusable validator for checking if a media field is filled in the CMS
export const isMediaFieldFilled = (value: unknown): boolean =>
  typeof value === "number" ||
  (typeof value === "object" &&
    value !== null &&
    !!(value as { url?: string }).url);

type FieldValidator<T> = {
  field: keyof T | string;
  validate: (value: unknown) => boolean;
};
